import React, {
  useState,
  useRef,
  useEffect,
  useMemo,
  useCallback,
} from "react";
import ReactPlayer from "react-player";
import "../../styles/videoPlayer.scss";
import { MdEventNote } from "react-icons/md";
import { FaBackward, FaForward, FaPause, FaPlay } from "react-icons/fa";
import { IoIosSkipBackward, IoIosSkipForward } from "react-icons/io";
import { useNavigate, useSearchParams } from "react-router-dom";
import CustomButton from "../reusable/CustomButton";
import CustomModal from "../reusable/CustomModal";
import CustomSelect from "../reusable/CustomSelect";
import bookService from "../../services/bookService";
import { useAuth } from "alpha-ui";
import { toast } from "react-toastify";

const styles = {
  playerWrapper: {
    position: "relative",
    width: "100%",
    height: "100%",
  },
  skipAnimation: {
    position: "absolute",
    top: "50%",
    transform: "translateY(-50%)",
    fontSize: "16px",
    zIndex: "1",
    color: "white",
    transition: "transform 0.5s ease",
  },
  progressBar: {
    display: "none",
  },
  timer: {
    display: "none",
  },
};

const CustomVideoPlayer = () => {
  const navigate = useNavigate();
  const playerRef = useRef(null);
  const [currentTime, setCurrentTime] = useState(0);
  const [duration, setDuration] = useState(0);
  const [isAddingNote, setIsAddingNote] = useState(false);
  const [noteText, setNoteText] = useState("");
  const [notes, setNotes] = useState([]);
  const [isPlaying, setIsPlaying] = useState(true);
  const [skipTime, setSkipTime] = useState(null);
  const [tapCount, setTapCount] = useState(0);
  const [lastTapTime, setLastTapTime] = useState(0);
  const [videoLinkById, setVideoLinkById] = useState("");

  const [searchParams] = useSearchParams();

  const streamId = searchParams.get("streamId");
  const videoId = searchParams.get("videoId");

  const handleProgress = (progress) => {
    setCurrentTime(progress.playedSeconds);
  };

  const handleSeek = (time) => {
    playerRef.current.seekTo(time);
    setCurrentTime(time);
  };

  const handleAddNote = () => {
    setIsAddingNote(!isAddingNote);
    playerRef.current?.seekTo(currentTime);
    setIsPlaying(!isPlaying);
    const addNotesDataElement = document.querySelector(".add_notes_data");
    if (addNotesDataElement) {
      addNotesDataElement.scrollIntoView({
        behavior: "smooth",
        block: "start",
      });
      window.scrollBy({ top: 100, behavior: "smooth" });
    }
  };

  const handleSaveNote = () => {
    setIsAddingNote(false);
    const newNote = { time: currentTime, text: noteText };
    setNotes([...notes, newNote]);
    setNoteText("");
    setIsPlaying(true);
  };

  const handleDuration = (duration) => {
    setDuration(duration);
  };

  const formatTime = (time) => {
    const hours = Math.floor(time / 3600);
    const minutes = Math.floor((time % 3600) / 60);
    const seconds = Math.floor(time % 60);

    return `${hours}:${minutes < 10 ? "0" + minutes : minutes}:${
      seconds < 10 ? "0" + seconds : seconds
    }`;
  };

  const togglePlayPause = () => {
    setIsPlaying(!isPlaying);
  };

  const handleSkipBackward = () => {
    const newTime = Math.max(currentTime - 15, 0);
    handleSeek(newTime);
    setSkipTime(-15);
    setTimeout(() => {
      setSkipTime(null);
    }, 500);
  };

  const handleSkipForward = () => {
    const newTime = Math.min(currentTime + 15, duration);
    handleSeek(newTime);
    setSkipTime(15);
    setTimeout(() => {
      setSkipTime(null);
    }, 500);
  };

  const handleContainerClick = (e) => {
    const currentTime = new Date().getTime();
    const timeDifference = currentTime - lastTapTime;

    if (timeDifference < 300 && tapCount === 1) {
      const rect = e.currentTarget.getBoundingClientRect();
      const clickPosition = e.clientX - rect.left;

      if (clickPosition <= rect.width / 2) {
        handleSkipBackward();
      } else {
        handleSkipForward();
      }

      setTapCount(0);
    } else {
      setTapCount(1);
      setLastTapTime(currentTime);
    }
  };

  const renderCustomControls = () => {
    return (
      <div className="audio_track_media">
        <div className="track_control">
          <button onClick={handleSkipBackward}>
            <IoIosSkipBackward />
          </button>
          <button onClick={togglePlayPause}>
            {!isPlaying ? <FaPlay /> : <FaPause />}
          </button>
          <button onClick={handleSkipForward}>
            <IoIosSkipForward />
          </button>
        </div>
        <div
          className="progress-bar"
          onClick={(e) => {
            const rect = e.target.getBoundingClientRect();
            const clickPosition = e.clientX - rect.left;
            const seekTime = (clickPosition / rect.width) * duration;
            handleSeek(seekTime);
          }}
        >
            <div 
          className="progress-indicator"
          style={{ width: `${(currentTime / duration) * 100}%` }}
        />
          {notes.map((note, index) => (
            <div
              key={index}
              className="note-dot"
              style={{ left: `${(note.time / duration) * 100}%` }}
            />
          ))}
          <div className="time-display">
            <span>{formatTime(currentTime)}</span> /{" "}
            <span>{formatTime(duration)}</span>
          </div>
        </div>
        <button onClick={handleAddNote}>
          <MdEventNote />
        </button>
      </div>
    );
  };

  const [show, setShow] = useState(false);
  const [booksLoading, setBooksLoading] = useState(false);
  const [bookList, setBookList] = useState([]);
  const [chapterLoading, setChapterLoading] = useState(false);
  const [bookChapterList, setBookChapterList] = useState([]);
  const [chapterData, setChapterData] = useState({});
  const [selectedBook, setSelectedBook] = useState("");
  const [selectedChapter, setSelectedChapter] = useState("");
  const [updateLoading, setUpdateLoading] = useState(false);
  const [updateVideoNoteLoading, setUpdateVideoNoteLoading] = useState(false);
  const {auth} = useAuth();

  // FETCH MY CREATED BOOKS START
  const fetchBookList = async () => {
    try {
      setBooksLoading(true);
      const res = await bookService.myAllBookList();
      if (res) {
        setBooksLoading(false);
        setBookList(res?.data?.books);
      }
    } catch (error) {
      setBooksLoading(false);
    }
  };

  useEffect(() => {
    if (auth?.user?._id) {
      fetchBookList();
    }
  }, [auth?.user?._id]);

  const optionBookList = useMemo(() => {
    return bookList?.map((ele) => ({
      option: ele?.name,
      value: ele?._id,
    }));
  }, [bookList]);
  // FETCH MY CREATED BOOKS ENDS

  // FETCH CHAPTERS BY BOOK ID START
  const getChapterListBybookId = async (id) => {
    setChapterLoading(true);
    try {
      const res = await bookService.myAllChapterbyBookid(id);
      if (res?.data?.success) {
        setBookChapterList(res?.data);
        setChapterLoading(false);
      }
    } catch (error) {
      setChapterLoading(false);
    }
  };

  useEffect(() => {
    if (selectedBook !== "") {
      getChapterListBybookId(selectedBook);
    }
  }, [selectedBook]);

  const chapterListFromBookId = useMemo(() => {
    return bookChapterList?.chapters?.map((ele) => ({
      option: ele?.name,
      value: ele?._id,
    }));
  }, [bookChapterList]);
  // FETCH CHAPTERS BY BOOK ID ENDS

  // FETCH CHAPTER CONTENT/DETAILS START
  const fetchData = useCallback(async () => {
    try {
      const res = await bookService.chapterDetailByChapterId(selectedChapter);
      if (res) {
        setChapterData(res?.data?.data?.[0]);
      }
    } catch (error) {
      toast.error(error);
    }
  }, [selectedChapter]);

  useEffect(() => {
    if (selectedChapter) {
      fetchData();
    }
  }, [selectedChapter, fetchData]);
  // FETCH CHAPTER CONTENT/DETAILS ENDS

  // UPDATE VIDEO IN SELECTED CHAPTER STRAT

  // SAVE VIDEO IN DATABASE WITH REFRENCE TO USER_ID
  const handleSaveVideo = async () => {
    setUpdateLoading(true);
    try {
      const res = await bookService.createUpdateVideoNote({
        title: "",
        link: streamId
          ? `https://www.youtube.com/embed/${streamId}`
          : videoLinkById || "",
        noteTime: notes || [],
      });
      if (res?.data?.success) {
        console.log("res", res?.data?.VideoNote);
       await handleSaveVideoInChapter(res?.data?.VideoNote?._id);
        setUpdateLoading(false);
      }
    } catch (error) {
      toast.error(error?.response?.data?.message);
      setUpdateLoading(false);
    } finally {
      setUpdateLoading(false);
    }
  };

  const handleSaveVideoInChapter = async (savedVideoId) => {
    setUpdateLoading(true);
    try {
      const res = await bookService.updateChapterData(
        chapterData,
        chapterData?.text,
        chapterData?.text +
          `<a href=${process.env.REACT_APP_SSB_FRONTEND_URL}/video?videoId=${savedVideoId} rel="noopener noreferrer" target="_blank"><img src="https://img.youtube.com/vi/${streamId}/hqdefault.jpg" alt="Video Thumbnail" /></a>`
        // ` <iframe
        //   src="https://www.youtube.com/embed/${streamId}"
        //   allowfullscreen
        //   style="width:100%; max-width:560px; height:315px; border:none; border-radius:8px; display:block;">
        // </iframe>`
      );
      if (res?.data?.success) {
        toast.success("Video Added");
        setShow(false);
        setUpdateLoading(false);
        setSelectedBook("");
        setSelectedChapter("");
      } else {
        toast.error(res?.data?.message);
        setUpdateLoading(false);
      }
    } catch (error) {
      toast.error(error?.response?.data?.message);
      setUpdateLoading(false);
    }
  };
  // UPDATE VIDEO IN SELECTED CHAPTER ENDS

  // GET NOTES DETAILS START
  console.log("videoId", videoId)
  const getVideoDetailsById = useCallback(async () => {
    try {
      const res = await bookService.getVideoNote(videoId);
      console.log("res", res)
      if (res?.data?.success) {
        console.log(res.data?.VideoNote);
        setVideoLinkById(res.data?.VideoNote?.link);
        setNotes(res.data?.VideoNote?.noteTime);
      }
    } catch (error) {
      console.log(error);
      toast.error(error?.response?.data?.message);
    }
  }, [videoId]);

  useEffect(() => {
    if (videoId) {
      getVideoDetailsById();
    }
  }, [videoId, getVideoDetailsById]);
  // GET NOTES DETAILS ENDS

  // UPDATE VIDEO NOTE START
  const updateVideoNote = async () => {
    setUpdateVideoNoteLoading(true);
    try {
      const res = await bookService.updateVideoNoteById(videoId,{
        title: "",
        link: videoLinkById || "",
        noteTime: notes || [],
      });
      if (res?.data?.success) {
        console.log("res", res?.data?.VideoNote);
        toast.success("Video updated")
        getVideoDetailsById();
        setUpdateVideoNoteLoading(false);
      }
    } catch (error) {
      toast.error(error?.response?.data?.message);
      setUpdateVideoNoteLoading(false);
    } finally {
      setUpdateVideoNoteLoading(false);
    }
  };
  // UPDATE VIDEO NOTE ENDS

  return (
    <div className="container">
      <div className="back_btn">
        <button onClick={() => navigate(-1)}>Go back</button>
      </div>
      <div className="video-container" onClick={handleContainerClick}>
        <div style={styles.playerWrapper}>
          <ReactPlayer
            ref={playerRef}
            playing={isPlaying}
            url={videoLinkById || `https://www.youtube.com/embed/${streamId}`}
            controls={false}
            onProgress={handleProgress}
            onDuration={handleDuration}
            // onClick={handleClickOnVideo()}
            width="100%"
            height="400px"
            minHeight="400px"
          />
          {skipTime !== null && (
            <div
              className="skip_animation"
              style={{
                ...styles.skipAnimation,
                left: skipTime < 0 ? "10%" : "85%",
              }}
            >
              <p>
                {skipTime < 0 && <FaBackward />}
                {Math.abs(skipTime)} sec
                {skipTime > 0 && <FaForward />}
              </p>
            </div>
          )}
          {renderCustomControls()}
        </div>

        {isAddingNote && (
          <div className="add_notes_data">
            <textarea
              value={noteText}
              rows={5}
              onChange={(e) => setNoteText(e.target.value)}
              placeholder="Add your note here..."
            />
            <button className="note_btn" onClick={handleSaveNote}>
              Save Note
            </button>
          </div>
        )}
        {notes?.length > 0 && (
          <div className="added_notes">
            <h3>Notes:</h3>
            <ul>
              {notes.map((note, index) => (
                <li key={index}>
                  <p>{formatTime(note.time)}</p>
                  <div>
                    <p>{note.text}</p>
                  </div>
                </li>
              ))}
            </ul>
          </div>
        )}

        {/* Add video in notes */}
        {videoId ? (
          <CustomButton
            className={`saveVideo_btn ${updateVideoNoteLoading ? "disabled" : ""}`}
            onClick={updateVideoNote}
            disabled={updateVideoNoteLoading}
            title={updateVideoNoteLoading ? "Loading..." : "Update"}
          />
        ) : (
          <CustomButton
            className="saveVideo_btn"
            onClick={() => setShow(true)}
            title="Save video in notes"
          />
        )}
      </div>

      <CustomModal show={show} setShow={() => setShow(false)}>
        <div className="saveVideoModal">
          <div className="title">Save Video</div>
          <CustomSelect
            label="Select Book"
            name="book"
            onChange={(e) => setSelectedBook(e.target.value)}
            optionArray={
              booksLoading ? [{ option: "LOADING...", value: "" }] : optionBookList
            }
            impStar="*"
            value={selectedBook}
            placeholder="Choose Option"
            borderColor="#f2f2f2"
          />
          <CustomSelect
            label="Select Chapter"
            name="chapter"
            onChange={(e) => setSelectedChapter(e.target.value)}
            optionArray={
              chapterLoading
                ? [{ option: "LOADING...", value: "" }]
                : chapterListFromBookId
            }
            impStar="*"
            readOnly={chapterLoading || booksLoading}
            value={selectedChapter}
            placeholder={chapterLoading ? "Loading..." : "Choose Option"}
            borderColor="#f2f2f2"
          />
          <CustomButton
            onClick={handleSaveVideo}
            className="modal_savebtn"
            title={updateLoading ? "Loading..." : "save"}
          />
        </div>
      </CustomModal>
    </div>
  );
};

export default CustomVideoPlayer;
